import React, { useState, useCallback, useId } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useStatsigClient } from '@statsig/react-bindings'
import { QueueItem, GroupedItems } from 'components'
import ModalActions from 'reducers/Modal'
import {
  isRelease,
  isPlaylist,
  thunks as PlayQueueThunks
} from 'reducers/PlayQueue'
import { AUTOPLAY_TOGGLE_TO_QUEUE } from 'constants/featureFlags'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import useAutoPlayToggle from 'utils/hooks/useAutoPlayToggle'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'

import {
  EmptyText,
  QueueText,
  QueueHeaderRow,
  QueueSectionTitle,
  QueueToggleContainer,
  QueueContainer,
  Row
} from './Queue.styles'
import { playQueueSelector } from './selectors'
import { Tonality, Toggle } from '@dsny/dsny-component-library'

const QueueView = () => {
  const autoPlayToggleId = useId()
  const theme = useTheme()
  const [selectedHoverItem, setSelectedHoverItem] = useState(null)
  const isMobile = useMobileMediaQuery()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const { autoPlay, hasNoSong, handleToggleAutoPlay } = useAutoPlayToggle()
  const {
    items,
    selectedItem,
    selectedSong,
    isPlaying,
    isIpadView,
    playQueue
  } = useSelector(playQueueSelector)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const shouldShowAutoplayInQueue =
    !isMobile &&
    !isStatsigLoading &&
    checkGate(AUTOPLAY_TOGGLE_TO_QUEUE)

  const handleClickClose = (item) => {
    dispatch(PlayQueueThunks.deleteItem(item, playQueue, isPlaying))
  }

  const handleClickPlay = (item) => {
    dispatch(
      PlayQueueThunks.seekToItem(item, {
        source: PLAY_SOURCES.QUEUE,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  const openClearQueueModal = useCallback(() => {
    dispatch(ModalActions.toggleClearQueueModal())
  }, [dispatch])

  const renderItem = (item) => {
    const isHighlighted = selectedSong && item.uuid === selectedSong.uuid
    if (isRelease(item)) {
      return (
        <GroupedItems
          key={`${item.uuid}`}
          item={item}
          title={item.title}
          message={item.artist}
          trackCount={item.songs.length}
          isHighlighted={selectedItem && item.uuid === selectedItem.uuid}
          isPlaying={isPlaying}
          selectedSong={selectedSong}
          isIpadView={isIpadView}
          selectedHoverItem={selectedHoverItem}
          setSelectedHoverItem={setSelectedHoverItem}
          onClickClose={handleClickClose}
          onClickPlay={handleClickPlay}
        />
      )
    } else if (isPlaylist(item)) {
      return (
        <GroupedItems
          key={`${item.uuid}`}
          item={item}
          title="Playlist"
          message={item.name}
          trackCount={item.songs.length}
          isPlaying={isPlaying}
          selectedSong={selectedSong}
          isIpadView={isIpadView}
          selectedHoverItem={selectedHoverItem}
          setSelectedHoverItem={setSelectedHoverItem}
          onClickClose={handleClickClose}
          onClickPlay={handleClickPlay}
          isHighlighted={isHighlighted}
        />
      )
    } else {
      return (
        <QueueItem
          key={`${item.uuid}`}
          item={item}
          title={item.title}
          artist={item.artist}
          isHighlighted={isHighlighted}
          showPlayingIcon={isHighlighted}
          isPlaying={isPlaying}
          isIpadView={isIpadView}
          onClickClose={() => handleClickClose(item)}
          onClickPlay={() => handleClickPlay(item)}
        />
      )
    }
  }

  const upNextTracks = items
    .filter((item) => item.uuid !== selectedSong?.uuid)
    .map((item, index) => {
      return renderItem(item, index)
    })

  return (
    <QueueContainer className="flex flex-col flex-grow">
      <QueueHeaderRow>
        <QueueSectionTitle>{t('sidebar.play-queue')}</QueueSectionTitle>
        {shouldShowAutoplayInQueue &&
          (hasNoSong ? (
            <QueueToggleContainer>
              <Toggle
                id={`${autoPlayToggleId}-disabled`}
                checked={autoPlay}
                onChange={handleToggleAutoPlay}
                disabled={true}
                setting="player"
                displayIcon={true}
              />
            </QueueToggleContainer>
          ) : (
            <Tonality.Tooltip
              mode="dark"
              placement="top"
              content={
                autoPlay
                  ? t('general.turnoff-autoplay')
                  : t('general.turnon-autoplay')
              }
              containerStyles={{ padding: '4px 8px' }}
            >
              <QueueToggleContainer>
                <Toggle
                  id={autoPlayToggleId}
                  checked={autoPlay}
                  onChange={handleToggleAutoPlay}
                  setting="player"
                  displayIcon={true}
                />
              </QueueToggleContainer>
            </Tonality.Tooltip>
          ))}
      </QueueHeaderRow>

      <>
        {!isEmpty(selectedSong) && (
          <div style={{ paddingTop: 8 }}>
            <QueueText>{t('sidebar.currently-playing')}</QueueText>
            <QueueItem
              key={`${selectedSong.uuid}`}
              item={selectedSong}
              title={selectedSong.title}
              artist={selectedSong.artist}
              isHighlighted
              showPlayingIcon
              isPlaying={isPlaying}
              onClickClose={() => handleClickClose(selectedSong)}
              onClickPlay={() => handleClickPlay(selectedSong)}
            />
          </div>
        )}

        {upNextTracks.length > 0 && (
          <>
            <Row
              style={{ justifyContent: 'space-between', margin: '8px 8px 0' }}
            >
              <Tonality.UIText
                textStyle="body"
                tag="strong"
                style={{ fontSize: 12, textTransform: 'uppercase' }}
                color={theme.colors.neutralW60}
              >
                {t('sidebar.upnext')}
              </Tonality.UIText>

              <Tonality.UIText
                textStyle="body"
                style={{ fontSize: 12, cursor: 'pointer' }}
                color={theme.colors.neutralW60}
                onClick={openClearQueueModal}
              >
                {t('home.clear-button-text')}
              </Tonality.UIText>
            </Row>

            <div className="pt-4 pb-24 overflow-y-auto">{upNextTracks}</div>
          </>
        )}
      </>

      {items.length === 0 && (
        <EmptyText>{t('sidebar.empty-queue-message')}</EmptyText>
      )}
    </QueueContainer>
  )
}

export default QueueView
